import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CommonDataTable from '../../../components/CommonDataTable';
import { fetchItemInventory, refreshWMSAPI } from '../../../services/item/item';
import { loadSkuRecords, lottableAt, lottableDateAt } from '../inventoryFieldValue';
import { clearHoldInventoryFocus, getHoldInventoryFocus, subscribeHoldInventoryFocus } from '../item-location/itemLocationFocus';

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const sameValue = (left, right) => String(left ?? '').trim() === String(right ?? '').trim();

const compareOwnerThenSku = (left, right) => {
  const byOwner = String(left?.owner ?? '').localeCompare(String(right?.owner ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  if (byOwner !== 0) return byOwner;
  return String(left?.item ?? '').localeCompare(String(right?.item ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

const hasOnHoldQty = (row) => Number(row?.onHold) > 0;

const applyTableFilters = (rows, filters) => {
  if (!Array.isArray(filters) || filters.length === 0) return rows;
  return rows.filter((row) => filters.every((rule) => {
    if (!rule?.columnId || rule.value == null || String(rule.value).trim() === '') return true;
    const value = String(row[rule.columnId] ?? '').toLowerCase();
    const expected = String(rule.value).toLowerCase();
    const operator = rule.operator || 'contains';
    if (operator === 'equals' || operator === '=') return value === expected;
    if (operator === 'startsWith') return value.startsWith(expected);
    if (operator === 'endsWith') return value.endsWith(expected);
    return value.includes(expected);
  }));
};

const HOLD_SUMMARY_CAT = 'hold_summary';

const fetchAllInventoryRows = async (filters = []) => {
  const size = 100;
  let pageNo = 0;
  let collected = [];
  let total = 0;

  do {
    const res = await fetchItemInventory(pageNo, size, filters, HOLD_SUMMARY_CAT);
    const list = Array.isArray(res?.data) ? res.data : [];
    total = Number(res?.totalItems ?? list.length);
    collected = collected.concat(list);
    pageNo += 1;
    if (list.length === 0) break;
  } while (collected.length < total && pageNo < 30);

  return collected;
};

const rawColumns = [
  { field: 'lot', headerName: 'Lot', width: 140 },
  { field: 'owner', headerName: 'Owner', width: 140 },
  { field: 'item', headerName: 'SKU ID', width: 160 },
  { field: 'description', headerName: 'Description', width: 220 },
  { field: 'location', headerName: 'Location', width: 140 },
  { field: 'onHold', headerName: 'On Hold', width: 110, type: 'number' },
  { field: 'holdStatus', headerName: 'Hold Status', width: 140 },
  { field: 'grossWeight', headerName: 'Gross Weight', width: 130, type: 'number' },
  { field: 'netWeight', headerName: 'Net Weight', width: 130, type: 'number' },
  { field: 'lottable01', headerName: 'Lottable 01', width: 130 },
  { field: 'lottable02', headerName: 'Lottable02', width: 130 },
  { field: 'lottable03', headerName: 'Lottable03', width: 130 },
  { field: 'manufacturingDate', headerName: 'Manufacturing Date', width: 170 },
  { field: 'expirationDate', headerName: 'Expiration Date', width: 160 },
  { field: 'lottable06', headerName: 'Lottable 06', width: 130 },
  { field: 'lottable07', headerName: 'Lottable 07', width: 130 },
  { field: 'lottable08', headerName: 'Lottable 08', width: 130 },
  { field: 'lottable09', headerName: 'Lottable 09', width: 130 },
  { field: 'lottable10', headerName: 'Lottable 10', width: 130 },
  { field: 'lottable11', headerName: 'Lottable 11', width: 130 },
  { field: 'lottable12', headerName: 'Lottable 12', width: 130 },
];

export default function HoldInventoryList() {
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [focusVersion, setFocusVersion] = useState(0);
  const focusedCacheRef = useRef(null);

  const mapHoldRow = useCallback((item, index) => ({
    id: item._id || `${item.skuId || 'item'}-${item.lot || item.location || index}`,
    lot: firstValue(item.lot, item.lotNo, item.batchNum, item.batch, item.batchLot),
    owner: item.owner ?? '',
    item: item.skuId ?? item.item ?? '',
    description: item.itemName ?? item.description ?? '',
    location: item.location ?? '',
    onHold: item.onHold ?? '',
    holdStatus: item.holdStatus ?? '',
    grossWeight: firstValue(item.grossWeight, item.gross, item.grossWgt),
    netWeight: firstValue(item.netWeight, item.net, item.netWgt),
    lottable01: lottableAt(item, 1),
    lottable02: lottableAt(item, 2),
    lottable03: lottableAt(item, 3),
    manufacturingDate: lottableDateAt(item, 4),
    expirationDate: lottableDateAt(item, 5),
    lottable06: lottableAt(item, 6),
    lottable07: lottableAt(item, 7),
    lottable08: lottableAt(item, 8),
    lottable09: lottableAt(item, 9),
    lottable10: lottableAt(item, 10),
    lottable11: lottableAt(item, 11),
    lottable12: lottableAt(item, 12),
  }), []);

  const fetchHandler = useCallback(async (pageNo, pageSizeNo, filters = [], options = {}) => {
    const focus = getHoldInventoryFocus();
    const focusKey = focus?.item ? String(focus.item).trim() : '';
    const filterKey = JSON.stringify(Array.isArray(filters) ? filters : []);
    const cacheKey = `${focusKey || '__hold__'}::${filterKey}`;

    try {
      if (options.refresh || !focusedCacheRef.current || focusedCacheRef.current.key !== cacheKey) {
        const list = await fetchAllInventoryRows(filters);
        await loadSkuRecords(list.map((item) => item.skuId || item.item));
        focusedCacheRef.current = {
          key: cacheKey,
          rows: list.map(mapHoldRow).filter((row) => (
            hasOnHoldQty(row) && (!focusKey || sameValue(row.item, focusKey))
          )),
        };
      }

      const matched = [...applyTableFilters(focusedCacheRef.current.rows, filters)].sort(compareOwnerThenSku);
      const start = pageNo * pageSizeNo;
      setTableRows(matched.slice(start, start + pageSizeNo));
      setTotalCount(matched.length);
    } catch (error) {
      console.error('Error fetching Hold Inventory data:', error);
    }
  }, [mapHoldRow]);

  useEffect(() => subscribeHoldInventoryFocus(() => {
    focusedCacheRef.current = null;
    setPage(0);
    setFocusVersion((version) => version + 1);
  }), []);

  useEffect(() => {
    fetchHandler(page, pageSize, appliedFilters);
  }, [fetchHandler, page, pageSize, appliedFilters, focusVersion]);

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);

  const handleSyncWms = useCallback(async () => {
    if (syncing) return;

    try {
      setSyncing(true);
      await refreshWMSAPI();
      focusedCacheRef.current = null;
      await fetchHandler(page, pageSize, appliedFilters, { refresh: true });
    } catch (error) {
      console.error('WMS Sync Error:', error);
      alert(
        error?.response?.data?.message ||
        'Failed to sync item inventory from WMS'
      );
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchHandler, page, pageSize, appliedFilters]);

  const allPossibleColumns = useMemo(() => rawColumns, []);
  const initialVisible = useMemo(() => allPossibleColumns.map((column) => column.field), [allPossibleColumns]);

  return (
    <CommonDataTable
      id="HoldInventory"
      allPossibleColumns={allPossibleColumns}
      initialVisible={initialVisible}
      initialRows={tableRows}
      height={520}
      rowsPerPageOptions={[10, 25, 50, 100]}
      exportImportPath="ItemInventory"
      exportQueryParams={{ cat: HOLD_SUMMARY_CAT }}
      forItems
      forInventory
      enableInlineColumnFilters
      showSyncWms
      onSyncWms={handleSyncWms}
      page={page}
      pageSize={pageSize}
      rowCount={totalCount}
      onPageChange={(newPage) => setPage(newPage)}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(0);
      }}
      onDataRefresh={handleDataRefresh}
      onManualRefresh={(payload) => {
        clearHoldInventoryFocus();
        focusedCacheRef.current = null;
        setPage(0);
        return fetchHandler(
          0,
          payload?.pageSize ?? pageSize,
          payload?.filters ?? appliedFilters,
          { refresh: true }
        );
      }}
    />
  );
}
